import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { translations } from "./translations";
import { initialLang, STORAGE_KEY } from "./i18nRoutes";

const LangContext = createContext(null);

// Public metadata for the language switcher. Keep this list ordered by
// customer volume — UA first (native buyer base), then DE (business
// residence), EN (default international fallback), PL & RU (rollout).
export const LANG_META = [
  { code: "ua", flag: "🇺🇦", native: "Українська", label: "Мова", html: "uk" },
  { code: "de", flag: "🇩🇪", native: "Deutsch",    label: "Sprache", html: "de" },
  { code: "en", flag: "🇬🇧", native: "English",    label: "Language", html: "en" },
  { code: "pl", flag: "🇵🇱", native: "Polski",     label: "Język", html: "pl" },
  { code: "ru", flag: "🇷🇺", native: "Русский",   label: "Язык", html: "ru" },
];

const PUBLIC_CODES = LANG_META.map((l) => l.code);
export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(initialLang);
  // Bumped only on an explicit user choice so the router can move to the
  // equivalent localized URL (URL-driven syncs never bump it).
  const [choiceVersion, setChoiceVersion] = useState(0);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = metaOf(lang).html;
  }, [lang]);

  const setLang = (next) => {
    if (!PUBLIC_CODES.includes(next)) return;
    setLangState(next);
    setChoiceVersion((v) => v + 1);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { void e; }
  };

  const syncLang = useCallback((next) => {
    if (PUBLIC_CODES.includes(next)) setLangState(next);
  }, []);

  // Missing entries in PL / RU dictionaries transparently fall back to EN
  // via `translations[lang] || translations.en || translations.de`.
  const t = translations[lang] || translations.en || translations.de;

  return (
    <LangContext.Provider value={{
      lang,
      setLang,
      syncLang,
      choiceVersion,
      t,
      langs: PUBLIC_CODES,
      allLangs: PUBLIC_CODES,
      meta: LANG_META,
      currentMeta: metaOf(lang),
    }}>
      {children}
    </LangContext.Provider>
  );
};

const metaOf = (code) => LANG_META.find((l) => l.code === code) || LANG_META.find((l) => l.code === "de");

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside LangProvider");
  return ctx;
};
